// client/src/components/MergeModal.js
// Generic merge confirmation modal used by both Firms and Contacts pages.
//
// Props:
//   records      — array of full record objects to merge (2+)
//   entityType   — 'firm' | 'contact'
//   primaryId    — currently selected primary ID (controlled)
//   onPrimaryChange(id) — called when user picks a different primary
//   onConfirm()  — called when user clicks Merge (caller fires the API)
//   onCancel()   — called on Cancel or backdrop click
//   merging      — bool, disables buttons while request is in flight
//   error        — string | null, shown as error banner
//
// The parent is responsible for the actual API call; this is display-only.

import { firmNames } from '../lib/contactFirms'

// (no React state needed — fully controlled by parent)

// ── helpers ───────────────────────────────────────────────────────────────

function formatAUM(n) {
  if (n == null) return null
  if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`
  if (n >= 1e9)  return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6)  return `$${Math.round(n / 1e6)}M`
  return `$${Number(n).toLocaleString()}`
}

function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── field rows for each entity type ──────────────────────────────────────

function firmFields(f) {
  return [
    { label: 'Type',     value: (f.firm_type ?? []).join(', ') || '—' },
    { label: 'HQ',       value: f.hq_location || '—' },
    { label: 'AUM',      value: formatAUM(f.aum_fund_size) || '—' },
    { label: 'Website',  value: f.website || '—' },
    { label: 'Tags',     value: (f.tags ?? []).join(', ') || '—' },
    { label: 'Notes',    value: f.notes ? (f.notes.length > 60 ? f.notes.slice(0, 60) + '…' : f.notes) : '—' },
  ]
}

function contactFields(c) {
  return [
    { label: 'Firms',    value: firmNames(c) || '—' },
    { label: 'Title',    value: c.title_role || '—' },
    { label: 'Email',    value: (c.email ?? []).join(', ') || '—' },
    { label: 'Phone',    value: (c.phone ?? []).join(', ') || '—' },
    { label: 'Type',     value: (c.contact_type ?? []).join(', ') || '—' },
    { label: 'Tags',     value: (c.tags ?? []).join(', ') || '—' },
    { label: 'Last seen',value: formatDate(c.last_interaction) || '—' },
  ]
}

// ── component ─────────────────────────────────────────────────────────────

export default function MergeModal({
  records,
  entityType,
  primaryId,
  onPrimaryChange,
  onConfirm,
  onCancel,
  merging,
  error,
}) {
  const mergeCount  = records.length - 1
  const getFields   = entityType === 'firm' ? firmFields : contactFields
  const idKey       = entityType === 'firm' ? 'firm_id' : 'contact_id'

  return (
    <div
      onClick={e => e.target === e.currentTarget && !merging && onCancel()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 16,
      }}
    >
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: Math.min(340 * records.length, 1100),
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.65)',
      }}>

        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>
              Merge {records.length} {entityType === 'firm' ? 'Firms' : 'Individuals'}
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              Select the record to keep as primary. The others will be merged into it.
            </p>
          </div>
          <button className="btn-icon" onClick={onCancel} disabled={merging}
            style={{ fontSize: 16, flexShrink: 0 }}>✕</button>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{
            margin: '12px 24px 0', padding: '10px 14px', borderRadius: 6,
            background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
            fontSize: 13, color: 'var(--danger-text)', flexShrink: 0,
          }}>{error}</div>
        )}

        {/* Comparison columns */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '20px 24px',
          display: 'grid',
          gridTemplateColumns: `repeat(${records.length}, 1fr)`,
          gap: 12, alignItems: 'start',
        }}>
          {records.map(rec => {
            const id        = rec[idKey]
            const isPrimary = id === primaryId
            const fields    = getFields(rec)
            return (
              <div
                key={id}
                onClick={() => onPrimaryChange(id)}
                style={{
                  border: `2px solid ${isPrimary ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 8, padding: '14px 16px', cursor: 'pointer',
                  background: isPrimary ? 'var(--accent-muted)' : 'var(--surface-raised)',
                  transition: 'border-color 0.15s, background 0.15s',
                  position: 'relative',
                }}
              >
                {/* Primary badge */}
                {isPrimary && (
                  <div style={{
                    position: 'absolute', top: -10, left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--accent)', color: '#fff',
                    fontSize: 10, fontWeight: 700, padding: '2px 8px',
                    borderRadius: 10, letterSpacing: '0.05em', whiteSpace: 'nowrap',
                  }}>PRIMARY (KEEP)</div>
                )}

                {/* Radio + name */}
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  cursor: 'pointer', marginBottom: 12,
                }}>
                  <input
                    type="radio"
                    name="merge-primary"
                    checked={isPrimary}
                    onChange={() => onPrimaryChange(id)}
                    style={{ accentColor: 'var(--accent)', marginTop: 2, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
                    {rec.name}
                  </span>
                </label>

                {/* Field rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {fields.map(({ label, value }) => (
                    <div key={label}>
                      <div style={{
                        fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                        letterSpacing: '0.06em', color: 'var(--text-secondary)',
                        marginBottom: 1,
                      }}>{label}</div>
                      <div style={{
                        fontSize: 12, color: value === '—' ? 'var(--text-muted)' : 'var(--text-primary)',
                        wordBreak: 'break-word',
                      }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* UUID hint */}
                <div style={{
                  marginTop: 10, fontSize: 10, color: 'var(--text-muted)',
                  fontFamily: 'monospace', wordBreak: 'break-all',
                }}>{id}</div>
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 420, lineHeight: 1.5 }}>
            The other {mergeCount} record{mergeCount !== 1 ? 's' : ''} will be merged into the primary and
            deleted. Their contacts, pipeline entries, and activity will be preserved.
          </p>
          <div style={{ display: 'flex', gap: 10, flexShrink: 0, marginLeft: 16 }}>
            <button className="btn btn-secondary" onClick={onCancel} disabled={merging}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={onConfirm} disabled={merging}>
              {merging ? 'Merging…' : `Merge ${records.length} Records`}
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}
